import { useEffect, useState } from 'react';
import { ArrowDown, GraduationCap, Phone, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import faceOne from '../../assets/faceone.jpg';
import faceTwo from '../../assets/facetwo.jpg';
import faceThree from '../../assets/face3.jpg';
import schoolOne from '../../assets/school1.jpg';
import schoolTwo from '../../assets/school2.jpg';
import schoolThree from '../../assets/school3.jpg';
import schoolFour from '../../assets/school4.jpg';
import heroMobile from '../../assets/heromobile.jpg';
import smartCampusHero from '../../assets/smart-campus-hero-optimized.webp';
import { SmartCampusNavigation, SocialIcon } from '../../components/landing/SmartCampusNavigation';
import { countryCurrencies } from '../../data/countryCurrencies';
import { formatCurrency } from '../../utils/currency';

const contactLinks = [
  { label: 'Instagram', href: 'https://www.instagram.com/', icon: 'instagram' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/', icon: 'linkedin' },
  { label: 'TikTok', href: 'https://www.tiktok.com/', icon: 'tiktok' },
  { label: 'WhatsApp', href: 'https://www.whatsapp.com/', icon: 'whatsapp' },
  { label: 'Call', href: 'tel:+254700000000', icon: 'phone' },
];
const homeBackgroundVideo = 'https://res.cloudinary.com/ubunifu/video/upload/v1607186910/pexels-rodnae-productions-5923291_jd1rwl.mp4';

export function LandingPage() {
  const navigate = useNavigate();
  const [homeCurrency, setHomeCurrency] = useState('KES');

  useEffect(() => {
    let cancelled = false;
    fetch('https://us-central1-smart-campus-africa.cloudfunctions.net/detectVisitorCountry')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Country detection failed'))))
      .then(({ countryCode }) => {
        if (cancelled) return;
        const currency = countryCurrencies[countryCode];
        if (currency) setHomeCurrency(currency);
      })
      .catch(() => {
        // Keep the Kenya pricing fallback when country detection is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function navigateToPlatform() {
    window.scrollTo(0, 0);
    navigate('/platform');
    window.requestAnimationFrame(() => window.scrollTo(0, 0));
  }

  function handleMobilePlatformScroll(event) {
    if (event.deltaY > 0) navigateToPlatform();
  }

  function handleMobilePlatformTouchStart(event) {
    event.currentTarget.dataset.touchStartY = event.touches[0].clientY;
  }

  function handleMobilePlatformTouchEnd(event) {
    const startY = Number(event.currentTarget.dataset.touchStartY);
    if (startY - event.changedTouches[0].clientY > 24) navigateToPlatform();
  }

  return (
    <main className="tennis-page">
      <section className="tennis-site" id="home" style={{ '--court-image': `url(${smartCampusHero})`, '--court-image-mobile': `url(${heroMobile})` }}>
        <video className="tennis-site-video tennis-site-video--mobile" autoPlay defaultMuted loop muted playsInline preload="auto" poster={heroMobile} onCanPlay={(event) => { event.currentTarget.muted = true; event.currentTarget.play().catch(() => {}); }} aria-hidden="true">
          <source src={homeBackgroundVideo} type="video/mp4" />
        </video>
        <SmartCampusNavigation landing />
        <div className="tennis-content">
          <div className="tennis-hero-copy" id="overview">
            <div className="tennis-community" id="smart-ai"><Sparkles size={21} />The platform behind modern education</div>
            <h1>Build. Teach.<br />Manage. Grow.</h1>
            <p>Run your school, academy, training center, university or independent teaching business from one platform, with admissions, learning, payments, staff, communication and AI working together.</p>
            <div className="tennis-subscribe"><p>Create your academy</p><Link className="tennis-subscribe-button" to="/login">Login</Link></div>
          </div>
          <aside className="tennis-info-stack" id="who-its-for" aria-label="Who can use SmartCampus">
            <article className="tennis-session-card" id="admissions"><span className="tennis-chip">SmartCampus Suite</span><h2><b>One</b> Platform<br />For <b>Every</b><br />Learning<br />Team</h2><Link to="/platform">Explore the platform</Link></article>
            <div className="tennis-trust" id="hr-staff">
              <span className="tennis-avatars" aria-hidden="true">
                <img src={faceOne} alt="" />
                <img src={faceTwo} alt="" />
                <img src={faceThree} alt="" />
              </span>
              Trusted by 2,300+<br />private tutors
            </div>
            <article className="tennis-price-card" id="fees-receipts"><header>Plans for every campus</header><div className="tennis-price-content"><div><span>From</span><strong>{formatCurrency(0, homeCurrency)}</strong><em>/Month</em></div><p className="tennis-price-note">Run your SmartCampus for free and only upgrade to the features you need.</p></div></article>
          </aside>
        </div>
        <div className="tennis-court-gallery" id="learning-live-classes">
          <span style={{ backgroundImage: `url(${schoolOne})` }} />
          <span style={{ backgroundImage: `url(${schoolTwo})` }} />
          <span style={{ backgroundImage: `url(${schoolThree})` }} />
          <span style={{ backgroundImage: `url(${schoolFour})` }} />
          <b>180+ Learning<br />Centers</b>
        </div>
        <div className="tennis-coaching-title"><GraduationCap size={48} /><h2>Serious growth.<br />Surprisingly simple.</h2></div>
        <footer className="tennis-footer" id="communication">
          <div className="tennis-contact">
            <span>Talk to us</span>
            <div className="tennis-contact-links" aria-label="Contact links">
              {contactLinks.map(({ label, href, icon }) => {
                const isPhone = icon === 'phone';
                return isPhone ? (
                  <a
                    key={label}
                    className="tennis-contact-link"
                    href={href}
                    aria-label={label}
                    title={label}
                  >
                    <Phone size={18} strokeWidth={2} />
                  </a>
                ) : (
                  <a
                    key={label}
                    className="tennis-contact-link"
                    href={href}
                    rel="noreferrer noopener"
                    target="_blank"
                    aria-label={label}
                    title={label}
                  >
                    <SocialIcon icon={icon} />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="tennis-footer-email">Email:&nbsp;&nbsp; info@smartcampus.africa</div>
          <Link
            className="tennis-mobile-platform-cta"
            onClick={navigateToPlatform}
            onTouchEnd={handleMobilePlatformTouchEnd}
            onTouchStart={handleMobilePlatformTouchStart}
            onWheel={handleMobilePlatformScroll}
            to="/platform"
            aria-label="Explore the SmartCampus platform"
          >
            <ArrowDown size={21} strokeWidth={1.8} />
          </Link>
        </footer>
      </section>
    </main>
  );
}
